import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Field, useForm } from 'react-final-form';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Unstable_Grid2 as Grid,
  Autocomplete,
  Chip,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import sessionTypeSlice from 'store/modules/sessionType';
import requestStatus from 'common/constants/status';
import SessionTypeCreateDialog from 'components/session-type/SessionTypeCreateDialog';
import StyledTextField from 'common/theme/fields/StyledTextfield';

function PlanCategoryDropdown() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { t } = useTranslation(['labels', 'buttons', 'common']);
  const form = useForm();
  const sessionTypes = useSelector((state) => state.sessionType.sessionTypeOptions);
  const status = useSelector((state) => state.sessionType.status);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState([]);

  useEffect(() => {
    // Cargar las categorías disponibles al montar el componente
    if (sessionTypes.length === 0 && status !== requestStatus.loading) {
      dispatch(sessionTypeSlice.actions.listAll());
    }
  }, [dispatch, sessionTypes.length, status]);

  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    // Recargar las categorías después de crear una nueva
    dispatch(sessionTypeSlice.actions.listAll());
  };

  const handleOpenDeleteDialog = () => {
    if (selectedForDeletion.length === 0) return;
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    await dispatch(sessionTypeSlice.actions.remove(selectedForDeletion));

    // Remover las categorías eliminadas del plan actual
    const currentCategories = form.getState().values.sessionTypeIds || [];
    const updatedCategories = currentCategories.filter(id => !selectedForDeletion.includes(id));
    form.change('sessionTypeIds', updatedCategories);

    setSelectedForDeletion([]);
    setOpenDeleteDialog(false);

    // Después de eliminar, recargar la lista
    setTimeout(() => {
      dispatch(sessionTypeSlice.actions.listAll());
    }, 500);
  };

  if (status === requestStatus.loading) {
    return (
      <Grid xs={12}>
        <Typography>{t('loading', { ns: 'common' }) || 'Cargando categorías...'}</Typography>
      </Grid>
    );
  }

  return (
    <Grid xs={12}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            {t('categories', { ns: 'labels' })}
          </Typography>
          {selectedForDeletion.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedForDeletion.length} {t('selected', { ns: 'common' }) || 'seleccionado(s)'}
              </Typography>
              <IconButton
                size="small"
                onClick={handleOpenDeleteDialog}
                sx={{
                  color: '#FFFFFF',
                  backgroundColor: '#d32f2f',
                  '&:hover': {
                    backgroundColor: '#b71c1c'
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
        <Button
          size="small"
          variant="contained"
          onClick={handleOpenCreateDialog}
          sx={{
            backgroundColor: 'primary.main',
            color: '#FFFFFF',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          }}
        >
          {t('add', { ns: 'buttons' })}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('planCategoriesDescription', { ns: 'labels' })}
      </Typography>

      <Field name="sessionTypeIds">
        {({ input }) => {
          // Convertir los IDs seleccionados a objetos completos para el Autocomplete
          const selectedOptions = (input.value || [])
            .map(id => sessionTypes.find(st => st.value === id))
            .filter(Boolean);

          return (
            <Autocomplete
              multiple
              id="categories-select"
              options={sessionTypes}
              value={selectedOptions}
              onChange={(event, newValue) => {
                // Convertir los objetos seleccionados a IDs
                const newIds = newValue.map(option => option.value);
                input.onChange(newIds);
                setSelectedForDeletion(newIds);
              }}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  label={t('selectCategories', { ns: 'labels' }) || 'Selecciona categorías'}
                  placeholder={selectedOptions.length === 0 ? (t('searchCategories', { ns: 'labels' }) || 'Buscar categorías...') : ''}
                  size={isDesktop ? 'medium' : 'small'}
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={option.label}
                    {...getTagProps({ index })}
                    sx={{
                      // Chip legible: texto e ícono de borrar en BLANCO sobre el chip (antes
                      // text.primary = negro en claro → invisible sobre el fondo del chip).
                      backgroundColor: 'primary.main',
                      color: '#FFFFFF',
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(255, 255, 255, 0.85)',
                        '&:hover': {
                          color: '#FFFFFF'
                        }
                      }
                    }}
                  />
                ))
              }
              sx={{
                '& .MuiAutocomplete-tag': {
                  margin: '2px'
                }
              }}
            />
          );
        }}
      </Field>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        {t('planCategoriesNote', { ns: 'labels' })}
      </Typography>

      <SessionTypeCreateDialog
        open={openCreateDialog}
        onClose={handleCloseCreateDialog}
      />

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('confirmDelete', { ns: 'common' }) || 'Confirmar eliminación'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('deleteCategoryConfirmation', { ns: 'common' }) || '¿Estás seguro de que deseas eliminar esta categoría?'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderColor: 'grey.400',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'grey.600',
                backgroundColor: 'grey.100'
              }
            }}
          >
            {t('cancel', { ns: 'buttons' })}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: '#d32f2f',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#b71c1c'
              }
            }}
          >
            {t('delete', { ns: 'buttons' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default PlanCategoryDropdown;
